import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTodosMonthlyReport, TodosClientMonthRow, getCompletedMonthIndexForTodo } from './useData';
import type { Todo } from '../../crm-todos/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReportPage } from '../../../components/reports/ReportPage';
import { exportReportToExcel, exportReportToPDF } from '../../../utils/reportExport';
import { XIcon } from '../../../components/ui/Icons';
import { formatDate, formatDateTime } from '../../../config/appConfig';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

type DrillState = { clientId: number | null; monthIndex: number | null } | null;

function MonthCell({
  count,
  onOpen,
}: {
  count: number;
  onOpen: () => void;
}) {
  if (count === 0) {
    return <span className="text-gray-400 dark:text-gray-500">-</span>;
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'text-sm font-medium rounded px-1 py-0.5 transition-colors',
        'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60'
      )}
      aria-label="View completed todos for this month"
    >
      {count}
    </button>
  );
}

const TodosMonthlyReportPage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const { data, todosInYear, isLoading, isError } = useTodosMonthlyReport(selectedYear);
  const [drillDown, setDrillDown] = useState<DrillState>(null);

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i).sort((a, b) => a - b);

  const sortedRows = useMemo(() => {
    return [...data].sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''));
  }, [data]);

  const totals = useMemo(() => {
    let grand = 0;
    const monthCounts: number[] = Array(12).fill(0);
    sortedRows.forEach((row) => {
      grand += row.totalCount;
      for (let i = 0; i < 12; i++) {
        monthCounts[i] += row.months[i]?.count ?? 0;
      }
    });
    return { grand, monthCounts };
  }, [sortedRows]);

  const reportTitle = `Todos · ${selectedYear}`;
  const fileName = `todos-monthly-report-${selectedYear}`;

  const onExportExcel = useCallback(async () => {
    const headers = ['#', 'Client', 'Completed (year)', ...MONTHS, 'Total'];
    const rows = sortedRows.map((row, i) => [
      i + 1,
      row.clientName ?? '',
      row.totalCount,
      ...MONTHS.map((_, j) => row.months[j]?.count ?? 0),
      row.totalCount,
    ]);
    if (rows.length > 0) {
      rows.push(['', 'Total', totals.grand, ...totals.monthCounts, totals.grand]);
    }
    await exportReportToExcel({ reportTitle, fileName, table: { headers, rows } });
  }, [sortedRows, totals, reportTitle, fileName]);

  const onExportPDF = useCallback(async () => {
    const headers = ['#', 'Client', 'Completed (year)', ...MONTHS, 'Total'];
    const rows = sortedRows.map((row, i) => [
      i + 1,
      row.clientName ?? '',
      row.totalCount,
      ...MONTHS.map((_, j) => row.months[j]?.count ?? 0),
      row.totalCount,
    ]);
    if (rows.length > 0) {
      rows.push(['', 'Total', totals.grand, ...totals.monthCounts, totals.grand]);
    }
    await exportReportToPDF({ reportTitle, fileName, table: { headers, rows } });
  }, [sortedRows, totals, reportTitle, fileName]);

  const drillDownTodos = useMemo((): Todo[] => {
    if (!drillDown) return [];
    return todosInYear.filter((t) => {
      const tid = t.clientId ?? null;
      if (tid !== drillDown.clientId) return false;
      if (drillDown.monthIndex === null) return true;
      return getCompletedMonthIndexForTodo(t) === drillDown.monthIndex;
    });
  }, [todosInYear, drillDown]);

  const drillTitle = useMemo(() => {
    if (!drillDown) return '';
    const name =
      sortedRows.find((r) => r.clientId === drillDown.clientId)?.clientName ?? 'Client';
    if (drillDown.monthIndex === null) {
      return `Completed · ${selectedYear} · ${name}`;
    }
    return `Completed · ${MONTHS[drillDown.monthIndex]} ${selectedYear} · ${name}`;
  }, [drillDown, selectedYear, sortedRows]);

  const yearFilters = (
    <>
      <span className={cn('text-xs font-medium mr-1', colors.textMuted)}>Year</span>
      <div
        className={cn('inline-flex rounded-lg p-1 gap-1.5 border', colors.border, colors.surface)}
        role="group"
        aria-label="Select year"
      >
        {yearOptions.map((y) => (
          <button
            key={y}
            type="button"
            onClick={() => setSelectedYear(y)}
            className={cn(
              'min-w-[2.75rem] px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors',
              selectedYear === y
                ? 'bg-indigo-600 text-white shadow-sm'
                : cn(colors.text, colors.surfaceHover, 'hover:bg-gray-100 dark:hover:bg-gray-700')
            )}
            aria-pressed={selectedYear === y}
          >
            {y}
          </button>
        ))}
      </div>
    </>
  );

  const openDrill = (row: TodosClientMonthRow, monthIndex: number | null) => {
    setDrillDown({ clientId: row.clientId, monthIndex });
  };

  return (
    <ReportPage
      filters={yearFilters}
      onExportExcel={onExportExcel}
      onExportPDF={onExportPDF}
      exportDisabled={sortedRows.length === 0}
      loading={isLoading}
      error={isError}
    >
      {sortedRows.length > 0 ? (
        <div className={cn('rounded-xl shadow-lg border overflow-hidden', colors.surface, colors.border)}>
          <div className="overflow-x-auto">
            <table className={cn('min-w-full divide-y border-collapse', colors.divide)}>
              <thead className={colors.surfaceHover}>
                <tr>
                  <th
                    className={cn(
                      'px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wider sticky left-0 z-10 min-w-[2.5rem] w-10 border-r',
                      colors.textMuted,
                      colors.surfaceHover,
                      colors.border
                    )}
                  >
                    #
                  </th>
                  <th
                    className={cn(
                      'px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wider min-w-[100px] max-w-[200px] w-[200px] sticky left-[2.5rem] z-10 border-r',
                      colors.textMuted,
                      colors.surfaceHover,
                      colors.border
                    )}
                  >
                    Client
                  </th>
                  <th
                    className={cn(
                      'px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wider min-w-[56px] border-r',
                      colors.textMuted,
                      colors.border
                    )}
                  >
                    Completed (year)
                  </th>
                  {MONTHS.map((month) => (
                    <th
                      key={month}
                      className={cn(
                        'px-1.5 py-2.5 text-right text-xs font-semibold uppercase tracking-wider min-w-[52px] border-r',
                        colors.textMuted,
                        colors.border
                      )}
                    >
                      {month}
                    </th>
                  ))}
                  <th
                    className={cn(
                      'px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wider min-w-[60px] sticky right-0 z-10',
                      colors.textMuted,
                      colors.surfaceHover
                    )}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className={cn('divide-y', colors.surface, colors.divide)}>
                {sortedRows.map((row, index) => (
                  <tr key={row.clientId ?? `row-${index}`} className={cn('transition-colors', colors.interactiveHover)}>
                    <td
                      className={cn(
                        'px-2 py-2 whitespace-nowrap text-sm text-center sticky left-0 z-10 border-r',
                        colors.textMuted,
                        colors.surface,
                        colors.border
                      )}
                    >
                      {index + 1}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-2 text-sm font-medium sticky left-[2.5rem] z-10 border-r max-w-[200px] break-words',
                        colors.text,
                        colors.surface,
                        colors.border
                      )}
                    >
                      {row.clientId != null ? (
                        <Link
                          to={`/clients/${row.clientId}`}
                          className="text-indigo-600 dark:text-indigo-400 hover:underline break-words"
                        >
                          {row.clientName}
                        </Link>
                      ) : (
                        row.clientName
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-2 whitespace-nowrap text-right text-sm font-medium border-r',
                        colors.text,
                        colors.border
                      )}
                    >
                      {row.totalCount}
                    </td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className={cn('px-1.5 py-2 whitespace-nowrap text-right text-sm border-r', colors.text, colors.border)}>
                        <MonthCell count={row.months[i]?.count ?? 0} onOpen={() => openDrill(row, i)} />
                      </td>
                    ))}
                    <td
                      className={cn(
                        'px-2 py-2 whitespace-nowrap text-right text-sm font-medium sticky right-0 z-10',
                        colors.primary,
                        colors.surface
                      )}
                    >
                      {row.totalCount > 0 ? (
                        <button
                          type="button"
                          onClick={() => openDrill(row, null)}
                          className={cn(
                            'font-medium rounded px-1 py-0.5 transition-colors',
                            'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60'
                          )}
                          aria-label="View all completed todos for this client in this year"
                        >
                          {row.totalCount}
                        </button>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className={cn('border-t-2', colors.surface, colors.divide)}>
                <tr className={cn('font-semibold', colors.surfaceHover)}>
                  <td
                    className={cn(
                      'px-2 py-2.5 text-center text-sm sticky left-0 z-10 border-r',
                      colors.textMuted,
                      colors.surface,
                      colors.border
                    )}
                  />
                  <td
                    className={cn(
                      'px-2 py-2.5 text-sm sticky left-[2.5rem] z-10 border-r',
                      colors.text,
                      colors.surface,
                      colors.border
                    )}
                  >
                    Total
                  </td>
                  <td className={cn('px-2 py-2.5 text-right text-sm border-r', colors.text, colors.border)}>
                    {totals.grand}
                  </td>
                  {MONTHS.map((_, i) => (
                    <td
                      key={i}
                      className={cn('px-1.5 py-2.5 text-right text-sm border-r', colors.text, colors.border)}
                    >
                      {(totals.monthCounts[i] ?? 0) === 0 ? (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      ) : (
                        totals.monthCounts[i]
                      )}
                    </td>
                  ))}
                  <td
                    className={cn(
                      'px-2 py-2.5 text-right text-sm sticky right-0 z-10',
                      colors.primary,
                      colors.surface
                    )}
                  >
                    {totals.grand}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className={cn('rounded-xl shadow-lg border p-8 text-center', colors.surface, colors.border)}>
          <svg className={cn('w-16 h-16 mx-auto mb-4', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          <p className={cn('text-lg font-medium', colors.textMuted)}>No completed todos for the selected year.</p>
          <p className={cn('text-sm mt-2', colors.textMuted)}>Try selecting a different year.</p>
        </div>
      )}

      {drillDown && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setDrillDown(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="todos-drill-title"
        >
          <div
            className={cn(
              'flex flex-col max-h-[85vh] w-full max-w-2xl rounded-xl shadow-xl overflow-hidden border',
              colors.surface,
              colors.border
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('flex-shrink-0 flex items-center justify-between px-4 py-3 border-b', colors.border)}>
              <h2 id="todos-drill-title" className={cn('text-lg font-semibold', colors.text)}>
                {drillTitle}
              </h2>
              <button
                type="button"
                onClick={() => setDrillDown(null)}
                className={cn('p-2 rounded-lg', colors.textMuted, colors.surfaceHover)}
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
              {drillDownTodos.length === 0 ? (
                <p className={cn('text-sm', colors.textMuted)}>No todos in this selection.</p>
              ) : (
                drillDownTodos.map((todo) => (
                  <div
                    key={todo.todoId}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors',
                      colors.border,
                      colors.surfaceHover,
                      'hover:ring-1 hover:ring-indigo-500/50'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          setDrillDown(null);
                          navigate(`/todos/${todo.todoId}`);
                        }}
                        className={cn(
                          'text-left w-full font-medium text-indigo-600 dark:text-indigo-400 hover:underline truncate block',
                          todo.todoDescr ? colors.text : colors.textMuted
                        )}
                      >
                        {todo.todoDescr || `Todo #${todo.todoId}`}
                      </button>
                      <div className={cn('flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs', colors.textMuted)}>
                        {todo.todoCompletedAt && <span>Completed: {formatDateTime(todo.todoCompletedAt)}</span>}
                        {todo.todoDueDate && <span>Due: {formatDate(todo.todoDueDate)}</span>}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <Link
                        to={`/todos/${todo.todoId}`}
                        onClick={() => setDrillDown(null)}
                        className={cn(
                          'p-2 rounded-lg',
                          colors.surfaceHover,
                          colors.textMuted,
                          'hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors'
                        )}
                        title="View"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </Link>
                      <Link
                        to={`/todos/${todo.todoId}/edit`}
                        onClick={() => setDrillDown(null)}
                        className={cn(
                          'p-2 rounded-lg',
                          colors.surfaceHover,
                          'text-indigo-600 dark:text-indigo-400 hover:ring-1 hover:ring-indigo-500/50 transition-colors'
                        )}
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 012.828-2.828L15 14.172V9a2 2 0 00-2-2h-2z"
                          />
                        </svg>
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </ReportPage>
  );
};

export default TodosMonthlyReportPage;
